
import React, {useCallback, useContext, useMemo, useRef, useState} from 'react';
import {SearchOutlined, ClearOutlined} from '@ant-design/icons';
import {Button, Input, Space, Table, theme, Card, Typography, ConfigProvider} from 'antd';
import Highlighter from 'react-highlight-words';
import {DarkModeContext} from "../../contexts/darkModeContext";
import './newDatatable.scss';

const { Text } = Typography;

export const NewDatatable = ({
                                 columns,
                                 rows,
                                 customRowClick,
                                 expandedRowRender = undefined,
                                 tableParams,
                                 setTableParams,
                                 loading,
                                 rowSelection,
                                 title
                             }) => {
    const { darkMode } = useContext(DarkModeContext);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const searchInput = useRef(null);

    const handleRowClick = customRowClick ? customRowClick : () => {};

    const handleExpandedRowsChange = (newExpandedRowKeys) => {
        if (newExpandedRowKeys.length > 0) {
            setExpandedRowKeys([newExpandedRowKeys[newExpandedRowKeys.length - 1]]);
        } else {
            setExpandedRowKeys([]);
        }
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = useCallback((dataIndex) => ({
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#667eea' : (darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'),
                    fontSize: '14px'
                }}
            />
        ),
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <Card
                size="small"
                className="search-dropdown-card"
                style={{
                    width: 280,
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                    borderRadius: 12,
                    boxShadow: darkMode
                        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                        : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    padding: '16px'
                }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                        Search in "{dataIndex}"
                    </Text>
                    <Input
                        ref={searchInput}
                        placeholder={`Enter search term...`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{
                            borderRadius: 8,
                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                        }}
                        suffix={<SearchOutlined style={{ color: '#667eea' }} />}
                    />
                    <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                                icon={<SearchOutlined/>}
                                size="small"
                                style={{
                                    borderRadius: 6,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    fontWeight: 500
                                }}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={() => clearFilters && handleReset(clearFilters)}
                                size="small"
                                icon={<ClearOutlined />}
                                style={{ borderRadius: 6 }}
                            >
                                Clear
                            </Button>
                        </Space>
                        <Button
                            type="text"
                            size="small"
                            onClick={() => close()}
                            style={{
                                borderRadius: 6,
                                color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                            }}
                        >
                            Close
                        </Button>
                    </Space>
                </Space>
            </Card>
        ),
        filterDropdownProps: {
            onOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        onFilter: (value, record) => {
            return record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase());
        },
        render: (text) => {
            return searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#667eea',
                        color: '#fff',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontWeight: 500
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                <span style={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                    fontSize: '13px'
                }}>
                    {text}
                </span>
            )
        },
    }), [searchedColumn, searchText, darkMode]);

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    const columns_dt = useMemo(() => columns.map((column) => {
        const enhancedColumn = {
            ...column,
            align: column.align || 'center',
            className: 'custom-table-cell',
            title: (
                <Text
                    strong
                    style={{
                        color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                        fontSize: '13px',
                        fontWeight: 600
                    }}
                >
                    {column.title}
                </Text>
            ),
        };

        if (column.search) {
            const {render: searchRender, onFilter: searchOnFilter, ...searchUiProps} = getColumnSearchProps(column.dataIndex);
            return {
                ...enhancedColumn,
                ...searchUiProps,
                render: column.render ? enhancedColumn.render : searchRender,
                onFilter: column.onFilter ? enhancedColumn.onFilter : searchOnFilter,
            };
        }
        return enhancedColumn;
    }), [columns, darkMode, getColumnSearchProps]);

    const onRow = useCallback((record) => ({
        onClick: () => handleRowClick(record),
    }), [handleRowClick]);

    return (
        <div className="enhanced-datatable">
            {/* 메인 테이블 */}
            <ConfigProvider theme={{algorithm: theme.compactAlgorithm}}>
                <Table
                    columns={columns_dt}
                    dataSource={rows}
                    pagination={tableParams.pagination}
                    onChange={handleTableChange}
                    style={{overflowX: "auto", margin: 10}}
                    rowKey={(record) => record.key}
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpandedRowsChange: handleExpandedRowsChange,
                    }}
                    onRow={onRow}
                    loading={loading}
                    size={'small'}
                    rowSelection={rowSelection}
                />
            </ConfigProvider>

        </div>
    );
};